import type { OptionsObject, SnackbarKey } from 'notistack';
import { useSnackbar as useBaseSnackbar } from 'notistack';

import { Or2Button } from '@/components/button/Or2Button';

interface ClosableSnackbarOptionsObject extends OptionsObject {
  dismissible?: boolean | undefined;
}

export type SnackbarFn = (
  message: React.ReactNode,
  opts?: ClosableSnackbarOptionsObject,
) => SnackbarKey;

export function useSnackbar() {
  const { enqueueSnackbar, closeSnackbar, ...other } = useBaseSnackbar();

  return {
    ...other,
    closeSnackbar,
    enqueueSnackbar: (
      message: React.ReactNode,
      opts?: ClosableSnackbarOptionsObject,
    ) => {
      const dismissible =
        opts && typeof opts.dismissible !== 'undefined'
          ? opts.dismissible
          : true;

      if (opts) {
        delete opts.dismissible;
      }

      if (opts && opts.variant === 'error' && opts.persist === undefined) {
        opts.persist = true;
      }

      function dismissAction(key: SnackbarKey) {
        return (
          <>
            {dismissible && closeSnackbar && (
              <Or2Button
                color={'inherit'}
                size={'small'}
                variant={'text'}
                onClick={() => {
                  closeSnackbar(key);
                }}
              >
                Close
              </Or2Button>
            )}
          </>
        );
      }

      const action = (key: SnackbarKey) => {
        return (
          <>
            {opts && opts.action
              ? typeof opts.action === 'function'
                ? opts.action(key)
                : opts.action
              : null}
            {dismissAction(key)}
          </>
        );
      };

      return enqueueSnackbar(message, {
        ...opts,
        anchorOrigin: { horizontal: 'center', vertical: 'top' },
        action,
      });
    },
  };
}
